/**
 * splitQuotedThread.js — DE/EN reply-marker vocabulary for email bodies.
 *
 * WHERE THIS SITS IN THE CHAIN
 * ----------------------------
 * `body_new` — the BACKEND splitter (`_split_email_thread`, surfaced through
 * `_bodyWithQuote` in toActivityEntry.js) — keeps precedence everywhere it
 * exists. This module is the FALLBACK for rows it never produced: pre-080
 * inbound emails, comm bodies, and threads whose client the backend
 * heuristics did not recognise. It never overrides a backend split.
 *
 * ONE marker vocabulary, TWO consumers: the plain-text splitter below, and
 * the HTML quote boundary in `sanitizeEmailHtml.js`, which walks the
 * SANITIZED DOM fragment and asks `isQuoteMarkerLine` about each top-level
 * block's text. Keeping the regexes in one place is why the HTML path can be
 * structural (`<blockquote>` + marker text) instead of class-based: our
 * sanitizer strips `class`, so `gmail_quote` does not survive to be matched.
 *
 * The bar for adding a marker: it must be emitted by a real client at the
 * START of its own line. Anything that can plausibly appear mid-prose
 * ("Gesendet von meinem iPhone", a bare "-----") is deliberately absent —
 * a false positive here hides the customer's actual words behind a toggle,
 * which is worse than showing an extra quoted paragraph.
 */

/**
 * Line-anchored reply/forward markers. Each is tested against a single
 * TRIMMED line, so `^`/`$` here mean "the whole line", not "the whole body".
 */
const MARKER_PATTERNS = [
  // Gmail / Apple Mail DE: "Am 12.03.2026 um 14:23 schrieb Max <max@x.de>:"
  /^Am\s+.{3,}\s+schrieb\s+.+:$/i,
  // Gmail / Apple Mail EN: "On …, X <x@y> wrote:"  ·  "On 12 Mar 2026, at 14:23, X wrote:"
  /^On\s+.{3,}\s+wrote:$/i,
  // Outlook separators, DE + EN (3+ dashes either side).
  /^-{3,}\s*(Urspr[üu]ngliche Nachricht|Original Message)\s*-{3,}$/i,
  // Forward banners, DE + EN.
  /^-{3,}\s*(Weitergeleitete Nachricht|Forwarded message)\s*-{3,}$/i,
  // Outlook block header — the first field of the block starts the quote.
  //
  // KNOWN FALSE POSITIVE, accepted with eyes open: this fires on ANY line
  // that begins "Von:" / "From:", including inside a hand-written staff note
  // ("Von: Kundendienst, telefonisch"). Since the fallback runs globally
  // (see toActivityEntry `_bodyWithQuote`), such a note grows a "Show quoted
  // message" toggle in the Audit tab. Nothing is lost — the full text is
  // behind the toggle — and the alternative, requiring a following
  // `Gesendet:`/`Sent:` line, would miss the many clients that emit the
  // block without one. Revisit if operators report it as noise.
  /^(Von|From):\s*\S.*$/,
  // Outlook's horizontal rule between reply and quote.
  /^_{8,}$/,
];

/**
 * True when a single line is a quote/forward boundary emitted by a mail
 * client. Leading/trailing whitespace is ignored; an embedded marker is NOT
 * a match (the pattern must own the whole line).
 *
 * @param {string} line
 * @returns {boolean}
 */
export function isQuoteMarkerLine(line) {
  if (typeof line !== 'string') return false;
  const trimmed = line.trim();
  if (!trimmed) return false;
  return MARKER_PATTERNS.some((re) => re.test(trimmed));
}

/** True for a line that is part of a `>`-prefixed quote block. */
function isQuotedLine(line) {
  return typeof line === 'string' && /^\s*>/.test(line);
}

/**
 * Split a plain-text email body into the new reply and the quoted thread.
 *
 * The split point is the FIRST marker line, or the first `>`-quoted line
 * when the client quoted without a banner. Everything from there on is the
 * quote — including the marker itself, so the collapsed block still says
 * whom it is quoting.
 *
 * Never returns an empty reply: if the marker sits at the top (the whole
 * message IS a quote), the body is handed back unsplit. Shortening a bubble
 * is the goal; emptying one is a regression.
 *
 * @param {string} text
 * @returns {{ reply: string, quoted: string|null }}
 */
export function splitQuotedText(text) {
  if (typeof text !== 'string' || !text) return { reply: '', quoted: null };

  const lines = text.split(/\r?\n/);
  const cut = lines.findIndex((ln) => isQuoteMarkerLine(ln) || isQuotedLine(ln));
  if (cut < 0) return { reply: text, quoted: null };

  const reply = lines.slice(0, cut).join('\n').replace(/\s+$/, '');
  if (!reply) return { reply: text, quoted: null };

  return { reply, quoted: lines.slice(cut).join('\n') };
}
